import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin } from "lucide-react";
import { useLang } from "../App";
import { propertiesI18n } from "../i18n";
import PageHeader from "../components/PageHeader";
import { commercialPricingMode, formatAvailability, formatCharges, formatPrice, formatPropertyPrice, isCommercialRental } from "./Properties";
import { SoldRibbon } from "../components/SoldRibbon";
import { resolveImg } from "../images";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PropertyDetail({ rental = false }) {
  const { lang, t } = useLang();
  const p = propertiesI18n[lang];
  const c = t.contact;
  const { id } = useParams();
  const [property, setProperty] = useState(undefined);
  const [imgIdx, setImgIdx] = useState(0);
  const [inq, setInq] = useState({ first_name: "", last_name: "", email: "", phone: "", message: "" });
  const [inqStatus, setInqStatus] = useState("idle");

  useEffect(() => {
    window.scrollTo(0, 0);
    setProperty(undefined);
    setImgIdx(0);
    fetch(`${API}/api/${rental ? "rentals" : "properties"}`)
      .then((r) => r.json())
      .then((d) => setProperty(d.properties.find((x) => x.id === id) || null))
      .catch(() => setProperty(null));
  }, [id, rental]);

  const submitInquiry = async (e) => {
    e.preventDefault();
    if (!property) return;
    setInqStatus("sending");
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...inq,
          project_type: rental ? p.rentalInquiryType : c.projectOptions[1],
          language: lang,
          property_ref: `${property.title} · ${property.zip_code} ${property.city}`,
        }),
      });
      if (!res.ok) throw new Error("failed");
      setInqStatus("success");
    } catch {
      setInqStatus("error");
    }
  };

  const backPath = rental ? "/rentals" : "/objekte";
  const backLabel = rental ? p.rentalBack : p.back;
  const shell = (children) => (
    <div className="min-h-screen bg-ivory text-ink">
      <PageHeader active={rental ? "rentals" : "properties"} />
      {children}
    </div>
  );

  if (property === undefined) return shell(<p className="px-6 py-24 text-sm text-ink/50 md:px-12 lg:px-20">…</p>);

  if (!property)
    return shell(
      <div data-testid="property-not-found" className="px-6 py-24 md:px-12 lg:px-20">
        <p className="font-serif text-2xl italic text-ink/60">{p.notFound}</p>
        <Link
          to={backPath}
          className="group mt-8 inline-flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.25em] text-ink/50 transition-colors duration-300 hover:text-bronze"
        >
          <ArrowLeft size={14} className="transition-transform duration-500 group-hover:-translate-x-1" /> {backLabel}
        </Link>
      </div>
    );

  const inqInput = "flen-input w-full";
  const isRented = rental && property.availability_status === "rented";
  const commercialRental = isCommercialRental(property);
  const commercialRateRental = commercialRental && commercialPricingMode(property) === "per_sqm_year";

  const desc = lang !== "fr" && property[`description_${lang}`] ? property[`description_${lang}`] : property.description;
  const feats = lang !== "fr" && (property[`features_${lang}`] || []).length > 0 ? property[`features_${lang}`] : property.features;
  const title = lang !== "fr" && property[`title_${lang}`] ? property[`title_${lang}`] : property.title;
  const extras = lang !== "fr" && (property[`extra_facts_${lang}`] || []).length > 0 ? property[`extra_facts_${lang}`] : property.extra_facts;

  const facts = [
    ...(property.ref ? [["Réf.", property.ref]] : []),
    [p.categoryLabel, p.categories[property.category]],
    property.floor ? [p.floorLabel, property.floor] : null,
    property.rooms ? [p.roomsLabel, `${property.rooms} ${p.roomsUnit}`] : null,
    property.usable_area ? [p.usableAreaLabel, `${property.usable_area} ${p.areaUnit}`] : null,
    property.land_area ? [p.landAreaLabel, `${property.land_area} ${p.areaUnit}`] : null,
    property.area && !commercialRental ? [p.livingAreaLabel, `${property.area} ${p.areaUnit}`] : null,
    property.year_built ? [p.yearBuiltLabel, property.year_built] : null,
    property.renovation_year ? [p.renovationLabel, property.renovation_year] : null,
    [p.locationFactLabel, `${property.zip_code} ${property.city}`],
    ...(extras || [])
      .map((x) => {
        const idx = x.includes(":") ? x.indexOf(":") : x.indexOf("=");
        if (idx < 1) return null;
        return [x.slice(0, idx).trim(), x.slice(idx + 1).trim()];
      })
      .filter(Boolean),
  ].filter(Boolean);

  return shell(
    <section data-testid={rental ? "rental-detail-page" : "property-detail-page"} className="px-6 pb-28 pt-12 md:px-12 lg:px-20">
      <Link
        to={backPath}
        data-testid="property-back"
        className="group inline-flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.25em] text-ink/50 transition-colors duration-300 hover:text-bronze"
      >
        <ArrowLeft size={14} className="transition-transform duration-500 group-hover:-translate-x-1" /> {backLabel}
      </Link>

      <div className="mt-10">
        <p className="text-[11px] uppercase tracking-[0.25em] text-bronze">
          {p.categories[property.category]} · {rental ? (isRented ? p.rented : p.rental) : property.sold ? p.sold : p.sale}
        </p>
        <h1 className="mt-4 max-w-3xl font-serif text-4xl font-medium leading-tight sm:text-5xl">{title}</h1>
        <p className="mt-4 flex items-center gap-2 text-sm font-light text-ink/55">
          <MapPin size={14} /> {property.zip_code} {property.city}
        </p>
      </div>

      {property.images && property.images.length > 0 && (
        <div className="mt-12">
          <div className="overflow-hidden bg-ink/5">
            <div className="relative w-full overflow-hidden">
              {property.sold ? (
                <SoldRibbon label={p.sold} testid="property-detail-sold" />
              ) : isRented ? (
                <SoldRibbon label={p.rented} testid="property-detail-rented" />
              ) : null}
              <img src={resolveImg(property.images[imgIdx])} alt={title} data-testid="property-main-image" className="h-auto max-h-[55vh] w-full object-contain md:max-h-[68vh]" />
            </div>
          </div>
          {property.images.length > 1 && (
            <div className="mt-4 flex gap-3 overflow-x-auto pb-2">
              {property.images.map((src, i) => (
                <button
                  key={src}
                  data-testid={`property-thumb-${i}`}
                  onClick={() => setImgIdx(i)}
                  className={`h-16 w-24 shrink-0 overflow-hidden border transition-all duration-300 ${
                    i === imgIdx ? "border-bronze" : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={resolveImg(src)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-16 grid gap-16 lg:grid-cols-[1fr_360px]">
        <div>
          <h2 className="text-[11px] font-medium uppercase tracking-[0.3em] text-bronze">{p.factsTitle}</h2>
          <dl className="mt-6 divide-y divide-ink/10 border-y border-ink/10" data-testid="property-facts">
            {facts.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-6 py-4 text-sm">
                <dt className="font-light text-ink/50">{k}</dt>
                <dd className="text-right font-medium">{v}</dd>
              </div>
            ))}
          </dl>

          {desc && (
            <div className="mt-16">
              <h2 className="text-[11px] font-medium uppercase tracking-[0.3em] text-bronze">{p.descriptionTitle}</h2>
              <p className="mt-6 max-w-2xl whitespace-pre-line text-base font-light leading-relaxed text-ink/70">
                {desc}
              </p>
            </div>
          )}

          {feats && feats.length > 0 && (
            <div className="mt-16">
              <h2 className="text-[11px] font-medium uppercase tracking-[0.3em] text-bronze">{p.featuresTitle}</h2>
              <ul className="mt-6 grid gap-x-10 sm:grid-cols-2" data-testid="property-features">
                {feats.map((f, i) => (
                  <li
                    key={i}
                    data-testid={`property-feature-${i}`}
                    className="flex items-center gap-3 border-b border-ink/10 py-3.5 text-sm font-light text-ink/70"
                  >
                    <span className="h-1 w-1 rotate-45 bg-bronze" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <aside className="lg:sticky lg:top-8 lg:self-start">
          <div className="border border-ink/15 p-8" data-testid="property-price-box">
            <p className="text-[10px] uppercase tracking-[0.25em] text-ink/45">{rental ? (commercialRateRental ? p.annualRentLabel : p.grossRentLabel) : p.priceLabel}</p>
            <p className="mt-2 font-serif text-3xl font-medium" data-testid="property-detail-price">
              {formatPropertyPrice(property, p)}
            </p>
            {rental && (
              <dl className="mt-6 space-y-3 border-t border-ink/10 pt-5 text-sm" data-testid="rental-price-breakdown">
                {commercialRateRental && Number(property.rental_price_per_sqm_year) > 0 && (
                  <div className="flex justify-between gap-4">
                    <dt className="font-light text-ink/50">{p.rentalRateLabel}</dt>
                    <dd className="font-medium" data-testid="rental-rate-sqm-year">{formatPrice(property.rental_price_per_sqm_year)} {p.perSqmYear}</dd>
                  </div>
                )}
                <div className="flex justify-between gap-4">
                  <dt className="font-light text-ink/50">{p.netRentLabel}</dt>
                  <dd className="font-medium" data-testid="rental-net-rent">{formatPrice(property.monthly_net_rent || 0)} {p.perMonth}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="font-light text-ink/50">{p.chargesLabel}</dt>
                  <dd className="font-medium" data-testid="rental-charges">{formatCharges(property, p)}</dd>
                </div>
                {commercialRateRental && (
                  <div className="flex justify-between gap-4">
                    <dt className="font-light text-ink/50">{p.grossRentLabel}</dt>
                    <dd className="font-medium" data-testid="rental-gross-rent">{formatPrice(property.monthly_gross_rent || 0)} {p.perMonth}</dd>
                  </div>
                )}
                {property.rental_deposit > 0 && (
                  <div className="flex justify-between gap-4 border-t border-ink/10 pt-3">
                    <dt className="font-light text-ink/50">{p.depositLabel}</dt>
                    <dd className="font-medium" data-testid="rental-deposit">{formatPrice(property.rental_deposit)}</dd>
                  </div>
                )}
              </dl>
            )}
            {(rental || property.availability) && (
              <div className="mt-6 border-t border-ink/10 pt-4">
                <p className="text-[10px] uppercase tracking-[0.25em] text-ink/45">{p.availabilityLabel}</p>
                <p className="mt-1 text-sm font-light" data-testid="property-availability">
                  {rental ? formatAvailability(property, p, lang) : property.availability}
                </p>
              </div>
            )}
          </div>

          <div className="mt-6 border border-bronze/40 p-8" data-testid="property-inquiry">
            {inqStatus === "success" ? (
              <div data-testid="inquiry-success">
                <h3 className="font-serif text-xl font-medium">{c.successTitle}</h3>
                <p className="mt-3 text-sm font-light text-ink/60">{c.successText}</p>
              </div>
            ) : (
              <form onSubmit={submitInquiry} data-testid="property-inquiry-form" className="space-y-6">
                <div>
                  <h3 className="font-serif text-2xl font-medium leading-snug">{p.inquiryTitle}</h3>
                  <p className="mt-2 text-xs font-light leading-relaxed text-ink/55">{p.inquirySub}</p>
                </div>
                <input
                  required
                  data-testid="inquiry-firstname"
                  placeholder={`${c.firstName} *`}
                  value={inq.first_name}
                  onChange={(e) => setInq({ ...inq, first_name: e.target.value })}
                  className={inqInput}
                />
                <input
                  required
                  data-testid="inquiry-lastname"
                  placeholder={`${c.lastName} *`}
                  value={inq.last_name}
                  onChange={(e) => setInq({ ...inq, last_name: e.target.value })}
                  className={inqInput}
                />
                <input
                  required
                  type="email"
                  data-testid="inquiry-email"
                  placeholder={`${c.email} *`}
                  value={inq.email}
                  onChange={(e) => setInq({ ...inq, email: e.target.value })}
                  className={inqInput}
                />
                <input
                  type="tel"
                  data-testid="inquiry-phone"
                  placeholder={c.phone}
                  value={inq.phone}
                  onChange={(e) => setInq({ ...inq, phone: e.target.value })}
                  className={inqInput}
                />
                <textarea
                  rows={4}
                  data-testid="inquiry-message"
                  placeholder={p.messagePlaceholder}
                  value={inq.message}
                  onChange={(e) => setInq({ ...inq, message: e.target.value })}
                  className={`${inqInput} resize-none`}
                />
                {inqStatus === "error" && <p className="text-sm text-red-800">{c.errorText}</p>}
                <button
                  type="submit"
                  data-testid="inquiry-submit"
                  disabled={inqStatus === "sending"}
                  className="group flex w-full items-center justify-center gap-3 bg-bronze px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-ivory transition-colors duration-500 hover:bg-champagne hover:text-ink disabled:opacity-50"
                >
                  {inqStatus === "sending" ? c.sending : c.submit}
                  <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
                </button>
              </form>
            )}
          </div>
        </aside>
      </div>
    </section>
  );
}
